import { FC, useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"

import { SearchableDropdown, SearchableDropdownOption } from "components/inputs/SearchableDropdown"

import { gameService } from "services/game/game.service"
import { GameInterface } from "services/game/type"
import { providersService } from "services/providers/providers.service"
import { ProviderRow } from "services/providers/type"

import { GameFilterType } from "types/tournament.types"

export interface GameFilterValue {
  game_filter_type: GameFilterType
  game_filter_ids: string[]
  provider_filter_ids: string[]
}

interface Props {
  value: GameFilterValue
  onChange: (next: GameFilterValue) => void
}

export const GameFilterSelect: FC<Props> = ({ value, onChange }) => {
  const { game_filter_type, game_filter_ids, provider_filter_ids } = value

  const [gameResults, setGameResults] = useState<SearchableDropdownOption[]>([])
  const [selectedGames, setSelectedGames] = useState<SearchableDropdownOption[]>([])
  const [gamesLoading, setGamesLoading] = useState(false)
  const [gamesSearch, setGamesSearch] = useState("")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const searchGames = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setGameResults([])
      return
    }
    setGamesLoading(true)
    try {
      const result = await gameService.getAllGames({ page: 1, pageSize: 30, search: query.trim(), includeDisabled: false })
      if (result?.games) {
        const opts: SearchableDropdownOption[] = result.games.map((game: GameInterface) => ({
          value: game.identifier,
          label: game.title || game.name || game.identifier,
          extra: game.producer
        }))
        const seen = new Set<string>()
        const unique = opts.filter((o) => {
          if (seen.has(o.value)) return false
          seen.add(o.value)
          return true
        })
        setGameResults(unique)
      }
    } catch {
      toast.error("Failed to search games")
    } finally {
      setGamesLoading(false)
    }
  }, [])

  // Debounce search input
  useEffect(() => {
    if (game_filter_type !== "SPECIFIC_GAMES") return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      searchGames(gamesSearch)
    }, 350)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [gamesSearch, game_filter_type, searchGames])

  const [providers, setProviders] = useState<SearchableDropdownOption[]>([])
  const [providersLoading, setProvidersLoading] = useState(false)
  const [providersSearch, setProvidersSearch] = useState("")
  const [providersLoaded, setProvidersLoaded] = useState(false)

  const loadProviders = useCallback(async () => {
    if (providersLoaded) return
    setProvidersLoading(true)
    try {
      const result = await providersService.getProviders({ page: 1, pageSize: 500, includeDisabled: false })
      if (result?.providers) {
        const opts: SearchableDropdownOption[] = result.providers.map((p: ProviderRow) => ({
          value: p.identifier,
          label: p.title || p.identifier
        }))
        setProviders(opts)
        setProvidersLoaded(true)
      }
    } catch {
      toast.error("Failed to load providers")
    } finally {
      setProvidersLoading(false)
    }
  }, [providersLoaded])

  useEffect(() => {
    if (game_filter_type === "PROVIDER") loadProviders()
  }, [game_filter_type, loadProviders])

  const handleTypeChange = (e: { target: { value: string } }) => {
    const newType = e.target.value as GameFilterType
    onChange({ game_filter_type: newType, game_filter_ids: [], provider_filter_ids: [] })
    setSelectedGames([])
    setGameResults([])
    setGamesSearch("")
  }

  const handleGameIdsChange = (ids: string[]) => {
    const allKnown = [...selectedGames, ...gameResults]
    const seen = new Set<string>()
    const updated = allKnown
      .filter((o) => ids.includes(o.value))
      .filter((o) => {
        if (seen.has(o.value)) return false
        seen.add(o.value)
        return true
      })
    setSelectedGames(updated)
    onChange({ ...value, game_filter_ids: ids })
  }

  const handleProviderIdsChange = (ids: string[]) => {
    onChange({ ...value, provider_filter_ids: ids })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Game Filter</label>
        <select
          value={game_filter_type}
          onChange={handleTypeChange}
          className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
        >
          <option value="ALL">All Games</option>
          <option value="SPECIFIC_GAMES">Specific Games</option>
          <option value="PROVIDER">By Provider</option>
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {game_filter_type === "ALL"
            ? "All games count towards the tournament."
            : game_filter_type === "SPECIFIC_GAMES"
            ? "Only selected games will count. Search by game name to find games."
            : "Only games from selected providers will count."}
        </p>
      </div>

      {game_filter_type === "SPECIFIC_GAMES" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Games</label>
          <SearchableDropdown
            mode="search"
            options={gameResults}
            selected={game_filter_ids}
            selectedOptions={selectedGames}
            onSelect={handleGameIdsChange}
            loading={gamesLoading}
            searchValue={gamesSearch}
            onSearchChange={setGamesSearch}
            placeholder="Type to search games by name..."
            emptyLabel="No games found"
            noResultsLabel="No matching games found"
            minSearchChars={2}
          />
          {game_filter_ids.length > 0 && (
            <p className="text-xs text-cyan-500 dark:text-cyan-400 mt-1 font-medium">
              {game_filter_ids.length} game{game_filter_ids.length !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>
      )}

      {game_filter_type === "PROVIDER" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Providers</label>
          <SearchableDropdown
            mode="preloaded"
            options={providers}
            selected={provider_filter_ids}
            onSelect={handleProviderIdsChange}
            loading={providersLoading}
            searchValue={providersSearch}
            onSearchChange={setProvidersSearch}
            placeholder="Search providers..."
            emptyLabel="Loading providers..."
            noResultsLabel="No matching providers found"
          />
          {provider_filter_ids.length > 0 && (
            <p className="text-xs text-cyan-500 dark:text-cyan-400 mt-1 font-medium">
              {provider_filter_ids.length} provider{provider_filter_ids.length !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>
      )}
    </div>
  )
}
